// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the reminder banner (calendar-plan.md section 3's timing and dismissal, section 12's browser/desktop
// rows for the text). For an event with the banner on:
// - timed: from start - reminder until start; with no reminder, from start until 15 min after ("· now");
// - all-day: from 00:00 of its first day (the calendar's timezone) minus the reminder until that day ends.
// A dismissal is kept on this device only (protected local storage, one map per user: event id -> the start it was
// dismissed at), so a moved event shows again. Nothing here makes a request. Exclude from upstream sync.

import {addDays, dayKeyOf, dayStart, formatDay, formatTime} from '@app/features/calendar/CalendarFormat';
import {CalendarBrowserStrings as B, fill} from '@app/features/calendar/CalendarStrings';
import type {CalendarEvent} from '@app/features/calendar/CalendarTypes';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {Logger} from '@app/features/platform/utils/AppLogger';

const logger = new Logger('CalendarBanner');

const MINUTE_MS = 60_000;
const NO_REMINDER_AFTER_START_MS = 15 * MINUTE_MS;

export type BannerDismissals = Readonly<Record<string, string>>;

/** The start a dismissal is tied to. */
export function bannerStartKey(event: CalendarEvent): string {
	return event.all_day ? `d:${event.start_date ?? ''}` : `t:${event.start_ms ?? ''}`;
}

export function reminderWindow(event: CalendarEvent, timezone: string): {from: number; to: number} | null {
	if (event.deleted || !event.banner) return null;
	const lead = (event.reminder_minutes ?? 0) * MINUTE_MS;
	if (event.all_day) {
		if (event.start_date == null) return null;
		const from = dayStart(event.start_date, timezone).toMillis() - lead;
		return {from, to: dayStart(addDays(event.start_date, 1), timezone).toMillis()};
	}
	if (event.start_ms == null) return null;
	if (event.reminder_minutes == null) {
		return {from: event.start_ms, to: event.start_ms + NO_REMINDER_AFTER_START_MS};
	}
	return {from: event.start_ms - lead, to: event.start_ms};
}

export function isDismissed(event: CalendarEvent, dismissals: BannerDismissals): boolean {
	return dismissals[event.id] === bannerStartKey(event);
}

/** The banners due now, soonest first. */
export function dueReminders(
	events: Iterable<CalendarEvent>,
	timezone: string,
	now: number,
	dismissals: BannerDismissals,
): Array<CalendarEvent> {
	const due: Array<{event: CalendarEvent; start: number}> = [];
	for (const event of events) {
		const window = reminderWindow(event, timezone);
		if (window == null || now < window.from || now >= window.to || isDismissed(event, dismissals)) continue;
		const start = event.all_day
			? dayStart(event.start_date as string, timezone).toMillis()
			: (event.start_ms as number);
		due.push({event, start});
	}
	return due.sort((a, b) => a.start - b.start || a.event.id.localeCompare(b.event.id)).map((entry) => entry.event);
}

/** The minutes until the start, rounded up: "<n> min", "<h> h", "<h> h <m> min", "1 day", "<n> days". */
export function relativeText(untilMs: number): string {
	const minutes = Math.max(1, Math.ceil(untilMs / MINUTE_MS));
	if (minutes < 60) return fill(B.relativeMinutes, {n: minutes});
	if (minutes < 1440) {
		const hours = Math.floor(minutes / 60);
		const rest = minutes % 60;
		return rest === 0 ? fill(B.relativeHours, {h: hours}) : fill(B.relativeHoursMinutes, {h: hours, m: rest});
	}
	const days = Math.floor(minutes / 1440);
	return days === 1 ? B.relativeOneDay : fill(B.relativeDays, {n: days});
}

export function bannerText(event: CalendarEvent, timezone: string, now: number): string {
	const todayKey = dayKeyOf(now, timezone);
	const tomorrowKey = addDays(todayKey, 1);
	if (event.all_day) {
		const dayKey = event.start_date ?? todayKey;
		if (dayKey <= todayKey) return fill(B.bannerAllDayToday, {title: event.title});
		if (dayKey === tomorrowKey) return fill(B.bannerAllDayTomorrow, {title: event.title});
		return fill(B.bannerAllDayLater, {title: event.title, date: formatDay(dayKey, todayKey)});
	}
	const start = event.start_ms ?? now;
	const time = formatTime(start, timezone);
	if (start <= now) return fill(B.bannerTimedNow, {title: event.title, time});
	const relative = relativeText(start - now);
	const dayKey = dayKeyOf(start, timezone);
	if (dayKey === todayKey) return fill(B.bannerTimedToday, {title: event.title, time, relative});
	if (dayKey === tomorrowKey) return fill(B.bannerTimedTomorrow, {title: event.title, time, relative});
	return fill(B.bannerTimedLater, {title: event.title, date: formatDay(dayKey, todayKey), time, relative});
}

/** Entries for events that are gone, moved or whose banner time is over are dropped. */
export function pruneDismissals(
	dismissals: BannerDismissals,
	events: ReadonlyMap<string, CalendarEvent>,
	timezone: string,
	now: number,
): Record<string, string> {
	const kept: Record<string, string> = {};
	for (const [eventId, startKey] of Object.entries(dismissals)) {
		const event = events.get(eventId);
		if (event == null || event.deleted || bannerStartKey(event) !== startKey) continue;
		const window = reminderWindow(event, timezone);
		if (window != null && now >= window.to) continue;
		kept[eventId] = startKey;
	}
	return kept;
}

const DISMISSED_KEY_PREFIX = 'fluxer.calendar_banner_dismissed';

function dismissedKey(userId: string): string {
	return `${DISMISSED_KEY_PREFIX}:${userId}`;
}

export interface BannerDismissalStorage {
	load(userId: string): Record<string, string>;
	save(userId: string, dismissals: BannerDismissals): void;
}

export const DeviceBannerDismissals: BannerDismissalStorage = {
	load(userId) {
		try {
			const raw = getProtectedLocalStorage()?.getItem(dismissedKey(userId));
			if (raw == null) return {};
			const parsed: unknown = JSON.parse(raw);
			if (typeof parsed !== 'object' || parsed === null) return {};
			return Object.fromEntries(
				Object.entries(parsed as Record<string, unknown>).filter(
					(entry): entry is [string, string] => typeof entry[1] === 'string',
				),
			);
		} catch (error) {
			logger.warn('Failed to read the calendar banner dismissals', error);
			return {};
		}
	},
	save(userId, dismissals) {
		try {
			getProtectedLocalStorage()?.setItem(dismissedKey(userId), JSON.stringify(dismissals));
		} catch (error) {
			logger.warn('Failed to persist the calendar banner dismissals', error);
		}
	},
};
